// src/components/CommandCenter.jsx
// Selma Intelligence Command Center -- Morning Briefing Dashboard

import React, { useState, useEffect } from 'react';
import './CommandCenter.css';
import TaskModal from './TaskModal';

// Stage vocabulary imported from canonical source (Workstream C — Cadence Engine Foundation)
import { ACTIVE_STAGES } from '../config/stages';
import { apiFetch } from '../lib/api';
import { completeQueueItem, dismissQueueItem, snoozeQueueItem } from '../lib/situationQueue';
import { useProfile } from '../context/UserProfileContext';

// -- Helpers --------------------------------------------------
const today = new Date();

// Deal size midpoint map (matches both Leads and Opportunities DBs)
const DEAL_SIZE_MAP = {
  '$100K-$300K':   200000,
  '$150K-$400K':   275000,
  '$200K-$400K':   300000,
  '$300K-$600K':   450000,
  '$400K-$750K':   575000,
  '$400K-$800K':   600000,
  '$500K-$1.2M':   850000,
  '$600K-$1M':     800000,
  '$800K-$1.5M':  1150000,
  '$1M+':         1500000,
};

function getDealValue(dealSize) {
  // If it's already a number, use it directly
  if (typeof dealSize === 'number') return dealSize;
  if (!isNaN(Number(dealSize)) && dealSize !== '' && dealSize != null) return Number(dealSize);
  // Legacy: map range strings
  return DEAL_SIZE_MAP[dealSize] || 0;
}

function formatCurrency(n) {
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${Math.round(n / 1000)}K`;
  return `$${n}`;
}

// -- Sub-components -------------------------------------------

function Card({ children, className = '', style = {} }) {
  return (
    <div className={`cc-card ${className}`} style={style}>
      {children}
    </div>
  );
}

function CardHeader({ icon, iconClass = 'cc-icon-blue', title, action, onAction }) {
  return (
    <div className="cc-card-header">
      <div className="cc-card-title">
        <div className={`cc-card-icon ${iconClass}`}>{icon}</div>
        {title}
      </div>
      {action && (
        <span className="cc-card-action" onClick={onAction}>{action}</span>
      )}
    </div>
  );
}


// ── The Moment ────────────────────────────────────────────────────────────────
function TheMoment({ moment, loading, onNavigate, onComplete, onDismiss }) {
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [expanded, setExpanded] = useState(true);

  if (loading) return (
    <div className="cc-moment cc-moment--loading">
      <div className="cc-moment-pulse" />
      <span className="cc-moment-loading-text">Victor is selecting today's focus...</span>
    </div>
  );

  if (!moment && !completed) return null;

  if (completed) return (
    <div className="cc-moment cc-moment--completed">
      <span className="cc-moment-check">✓</span>
      <span className="cc-moment-completed-text">You engaged with <strong>{moment?.accountName}</strong> today.</span>
    </div>
  );

  const CONF_COLOR = { High: '#639922', Medium: '#BA7517', Low: '#A32D2D' };
  const confDot = (conf) => ({
    display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 5,
    background: CONF_COLOR[conf] || 'var(--dm-grey-500)',
  });

  const handleAct = async () => {
    setCompleting(true);
    // Mark complete in DB
    if (moment.id) {
      apiFetch('/api/jarvis/the-moment', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'complete', momentId: moment.id }),
      }).catch(() => {});
    }
    // Fade to completed state
    setCompleted(true);
    setCompleting(false);
    if (onComplete) onComplete(moment);
    // Open The Floor with account context
    onNavigate && onNavigate('floor', null, {
      type: 'account',
      accountId: moment.accountId,
      accountName: moment.accountName,
    });
  };

  const handleDismiss = async () => {
    if (moment.id) {
      const res = await apiFetch('/api/jarvis/the-moment', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'dismiss', momentId: moment.id }),
      }).then(r => r.json()).catch(() => null);
      if (res?.moment && onDismiss) onDismiss(res.moment);
    }
  };

  return (
    <div className={`cc-moment ${completing ? 'cc-moment--completing' : ''}`}>
      <div className="cc-moment-header">
        <div className="cc-moment-label">
          <span className="cc-moment-pulse-dot" />
          <span className="cc-moment-label-text">The Moment</span>
          {moment.isFocus && <span className="cc-moment-focus-badge">Focus</span>}
        </div>
        <div className="cc-moment-header-actions">
          <button className="cc-moment-toggle" onClick={() => setExpanded(e => !e)}>
            {expanded ? '↑' : '↓'}
          </button>
          <button className="cc-moment-dismiss" onClick={handleDismiss} title="Not today">
            ✕
          </button>
        </div>
      </div>

      {expanded && (
        <>
          <div className="cc-moment-account">{moment.accountName}</div>
          <div className="cc-moment-narrative">{moment.narrative}</div>

          {moment.opportunities?.length > 0 && (
            <div className="cc-moment-opps">
              {moment.opportunities.map((opp, i) => (
                <div key={opp.id || i} className="cc-moment-opp">
                  <div className="cc-moment-opp-name">
                    <span style={confDot(opp.victorConfidence)} />
                    {opp.name}
                  </div>
                  <div className="cc-moment-opp-meta">
                    <span className="cc-moment-opp-stage">{(opp.stage || '').split(' - ')[1] || opp.stage}</span>
                    {opp.meddicCompleteness > 0 && (
                      <span className="cc-moment-opp-meddic">MEDDIC {opp.meddicCompleteness}%</span>
                    )}
                    {opp.daysSinceUpdate != null && opp.daysSinceUpdate > 7 && (
                      <span className="cc-moment-opp-stale">{opp.daysSinceUpdate}d stale</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {moment.signals?.length > 0 && (
            <div className="cc-moment-signals">
              {moment.signals.map((s, i) => (
                <span key={i} className="cc-moment-signal-pill">📡 {s.title}</span>
              ))}
            </div>
          )}

          <div className="cc-moment-actions">
            <button className="cc-moment-act-btn" onClick={handleAct} disabled={completing}>
              {completing ? '⏳' : `⚡ Engage ${moment.accountName} →`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Center Stage full-play modal ──────────────────────────────────────────────
function CenterStageModal({ brief, onClose, onNavigate }) {
  const [sigOpen, setSigOpen] = useState(false);
  if (!brief) return null;
  const opps     = brief.opportunities || [];
  const execs    = brief.targeted_execs || [];
  const content  = brief.recommended_content || [];
  const score    = brief.score_breakdown?.final;
  const nSignals = brief.trigger_signal_ids?.length || 0;

  const toFloor = () => {
    onClose && onClose();
    onNavigate && onNavigate('floor', null, { type: 'account', accountId: brief.account_id, accountName: brief.account_name });
  };
  const askVictor = () => {
    onClose && onClose();
    onNavigate && onNavigate('floor', null, {
      type: 'account', accountId: brief.account_id, accountName: brief.account_name,
      autoMessage: `Victor, walk me through today's Center Stage play for ${brief.account_name}. Your read was: "${brief.why_now?.summary || ''}" — where should I start and what would you adjust?`,
    });
  };

  return (
    <div className="cc-cs-overlay" onClick={onClose}>
      <div className="cc-cs-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cc-cs-modal-head">
          <div>
            <div className="cc-cs-modal-title">{brief.account_name} — Center Stage</div>
            <div className="cc-cs-modal-sub">Victor's play for today{score != null ? ` · score ${Number(score).toFixed(1)}` : ''}{nSignals ? ` · ${nSignals} new signals` : ''}</div>
          </div>
          <button className="cc-cs-modal-x" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="cc-cs-modal-body">
          {brief.why_now?.summary && (<>
            <div className="cc-cs-label">Why now</div>
            <div className="cc-cs-para">{brief.why_now.summary}</div>
          </>)}

          {Array.isArray(brief.trigger_signals) && brief.trigger_signals.length > 0 && (<>
            <button
              type="button"
              className={`cc-cs-disclosure${sigOpen ? ' is-open' : ''}`}
              onClick={() => setSigOpen(o => !o)}
              aria-expanded={sigOpen}
            >
              <span className="cc-cs-disclosure-arrow" aria-hidden="true">▸</span>
              <span className="cc-cs-label cc-cs-label--inline">Source signals — the raw facts behind this play ({brief.trigger_signals.length})</span>
            </button>
            {sigOpen && (<>
              <div className="cc-cs-sig-list">
                {brief.trigger_signals.map((s, i) => (
                  <div key={s.id || i} className="cc-cs-sig">
                    <div className="cc-cs-sig-head">
                      <span className="cc-cs-sig-title">{s.title}</span>
                      {s.strength && <span className="cc-cs-sig-strength">{s.strength}</span>}
                      {s.observed_at && <span className="cc-cs-sig-date">{String(s.observed_at).slice(0, 10)}</span>}
                    </div>
                    {s.source && <div className="cc-cs-sig-src">{s.source}</div>}
                  </div>
                ))}
              </div>
              <div className="cc-cs-sig-note">Victor's read above is synthesized from these signals. Any claim not grounded in your data is tagged [UNVERIFIED] in the text.</div>
            </>)}
          </>)}

          {brief.account_read && (<>
            <div className="cc-cs-label">Strategic read</div>
            <div className="cc-cs-para">{brief.account_read}</div>
          </>)}

          {opps.length > 0 && (<>
            <div className="cc-cs-label">Plays ({opps.length})</div>
            {opps.map((o, i) => (
              <div key={i} className="cc-cs-block">
                <div className="cc-cs-block-title">
                  {o.is_off_radar && <span className="cc-cs-tag cc-cs-tag--offradar">OFF-RADAR</span>}
                  {o.opportunity_name || 'Opportunity'}
                </div>
                {o.strategy && <div className="cc-cs-block-text">{o.strategy}</div>}
                {o.meddic_gap && <div className="cc-cs-gap"><strong>Gap:</strong> {o.meddic_gap}</div>}
                {Array.isArray(o.next_actions) && o.next_actions.length > 0 && (
                  <ul className="cc-cs-actions-list">
                    {o.next_actions.map((a, j) => <li key={j}>{a}</li>)}
                  </ul>
                )}
              </div>
            ))}
          </>)}

          {execs.length > 0 && (<>
            <div className="cc-cs-label">Targeted execs ({execs.length})</div>
            <div className="cc-cs-exec-grid">
              {execs.map((e, i) => (
                <div key={i} className="cc-cs-exec">
                  <div className="cc-cs-exec-name">{e.name}</div>
                  {e.title && <div className="cc-cs-exec-title">{e.title}</div>}
                  {e.why && <div className="cc-cs-exec-text">{e.why}</div>}
                  {e.recommended_approach && <div className="cc-cs-exec-approach">{e.recommended_approach}</div>}
                </div>
              ))}
            </div>
          </>)}

          {content.length > 0 && (<>
            <div className="cc-cs-label">Recommended content ({content.length})</div>
            <div className="cc-cs-content-list">
              {content.map((c, i) => (
                <div key={i} className="cc-cs-content-row">
                  <span className="cc-cs-content-type">{(c.type || '').toUpperCase()}</span>
                  <span className="cc-cs-content-text">{c.for ? `${c.for} — ` : ''}{c.angle}</span>
                </div>
              ))}
            </div>
          </>)}

          {brief.selma_research_note && brief.selma_research_note !== 'no new intel surfaced' && (
            <div className="cc-cs-selma"><strong>Research next:</strong> {brief.selma_research_note}</div>
          )}
        </div>

        <div className="cc-cs-modal-foot">
          <button className="cc-cs-btn cc-cs-btn--execute" onClick={toFloor}>Take to The Floor</button>
          <button className="cc-cs-btn cc-cs-btn--modify" onClick={askVictor}>Ask Victor to adjust</button>
          <button className="cc-cs-btn cc-cs-btn--ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── 2x2 Command Center ────────────────────────────────────────────────────────
// Past Findings library — lists prior Center Stage briefs; clicking one opens it
// in the existing CenterStageModal. Presentational (no hooks); parent owns state.
function CenterStageLibrary({ items, loading, onPick, onClose }) {
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
    catch { return ''; }
  };
  return (
    <div className="cc-cslib-overlay" onClick={onClose}>
      <div className="cc-cslib-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cc-cslib-header">
          <div className="cc-cslib-title">🎯 Past Findings</div>
          <button className="cc-cslib-close" onClick={onClose}>✕</button>
        </div>
        <div className="cc-cslib-body">
          {loading ? (
            <div className="cc-cslib-empty">Loading…</div>
          ) : !items || items.length === 0 ? (
            <div className="cc-cslib-empty">No past findings yet.</div>
          ) : (
            items.map((it) => (
              <button key={it.sessionId} className="cc-cslib-item" onClick={() => onPick(it.brief)}>
                <div className="cc-cslib-item-top">
                  <span className="cc-cslib-item-account">{(it.brief && it.brief.account_name) || 'Account'}</span>
                  <span className="cc-cslib-item-date">{fmt(it.generatedAt)}</span>
                </div>
                <div className="cc-cslib-item-hook">{(it.brief && it.brief.why_now && it.brief.why_now.summary) || ''}</div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function CommandGrid({ opportunities, accounts, engagePeople, overnightItems, pipelineOpps, onNavigate, theMoment, centerStage, onOpenCenterStage, onOpenLibrary, onSelectPerson }) {
  const { firstName } = useProfile();

  const hour = today.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  // ── Derive Top Opportunities — ranked by api/shared/rank-opportunities ──────
  // priorityScore is attached server-side in api/opportunities/list.js so the
  // ordering is the single source of truth across all consumers.
  // Filter out closed/lost/under-review stages, then take top 3 by score.
  const EXCLUDED = ['0% - Deal Lost', 'Reviewed / Not Advancing', '100% - Deal Signed', 'Under Review'];
  const topOpps = [...(opportunities || [])]
    .filter(o => !EXCLUDED.some(ex => (o.probability || '').includes(ex)))
    .map(o => {
      const daysToClose = o.expectedClose
        ? Math.max(0, Math.ceil((new Date(o.expectedClose) - Date.now()) / 86400000))
        : null;
      return { ...o, _daysToClose: daysToClose };
    })
    .sort((a, b) => (b.priorityScore || 0) - (a.priorityScore || 0))
    .slice(0, 3);

  // ── Derive At-Risk Deals (stale + low MEDDIC) ────────────────────────────
  // ACTIVE_STAGES imported from canonical source (src/config/stages.js).
  const atRisk = [...(opportunities || [])]
    .filter(o => ACTIVE_STAGES.includes(o.probability))
    .map(o => {
      const hasDate = !!o.updatedAt;
      const daysSinceUpdate = hasDate
        ? Math.floor((Date.now() - new Date(o.updatedAt).getTime()) / 86400000)
        : null;
      const meddicPct = o.meddicCompleteness || 0;
      // Risk score: stale + low MEDDIC
      const riskScore = (daysSinceUpdate === null ? 2 : daysSinceUpdate > 21 ? 3 : daysSinceUpdate > 14 ? 2 : daysSinceUpdate > 7 ? 1 : 0)
                      + (meddicPct < 30 ? 2 : meddicPct < 50 ? 1 : 0);
      const riskReason = daysSinceUpdate === null
        ? 'No activity logged'
        : daysSinceUpdate > 21
        ? `No activity ${daysSinceUpdate}d`
        : meddicPct < 30
        ? 'MEDDIC incomplete'
        : `Stale ${daysSinceUpdate}d`;
      return { ...o, riskScore, riskReason, daysSinceUpdate };
    })
    .filter(o => o.riskScore >= 2)
    .sort((a, b) => b.riskScore - a.riskScore)
    .slice(0, 3);

  return (
    <div className="cc-command-wrapper">
      {/* ── Greeting header ─────────────────────────────────────────────── */}
      <div className="cc-command-header">
        <div className="cc-command-greeting">
          <span className="cc-command-greeting-icon">
            {hour < 12 ? '☀️' : hour < 17 ? '🌤️' : '🌙'}
          </span>
          <div>
            <div className="cc-command-greeting-text">{firstName ? `${greeting}, ${firstName}.` : `${greeting}.`}</div>
            <div className="cc-command-greeting-sub">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </div>
          </div>
        </div>
        <div className="cc-command-header-actions">
          <button className="cc-command-today-btn" onClick={() => onNavigate && onNavigate('today')}>
            📋 Today Studio →
          </button>
        </div>
      </div>{/* /cc-command-header */}

      {/* ── 2x2 Grid ────────────────────────────────────────────────────── */}
      <div className="cc-grid-2x2">

        {/* Q1 — Center Stage: The Moment (Jarvis-selected daily focus account) */}
        <div className="cc-grid-cell cc-grid-cell--priorities">
          <div className="cc-grid-cell-header">
            <div className="cc-grid-cell-icon cc-grid-icon--blue">🎯</div>
            <div className="cc-grid-cell-title">Center Stage</div>
            <button className="cc-cslib-btn" onClick={() => onOpenLibrary && onOpenLibrary()}>
              Past findings
            </button>
          </div>
          <div className="cc-grid-cell-body">
            {centerStage ? (
              <div className="cc-cs-card">
                <div className="cc-cs-account">{centerStage.account_name}</div>
                <div className="cc-cs-foundtag">Victor found something</div>
                <div className="cc-cs-hook">{centerStage.why_now?.summary}</div>
                <div className="cc-cs-meta">
                  <span><strong>{centerStage.trigger_signal_ids?.length || 0}</strong> new signals</span>
                  <span><strong>{centerStage.opportunities?.length || 0}</strong> plays</span>
                  <span><strong>{centerStage.targeted_execs?.length || 0}</strong> execs</span>
                </div>
                <div className="cc-cs-card-actions">
                  <button className="cc-cs-btn cc-cs-btn--primary"
                    onClick={() => onOpenCenterStage && onOpenCenterStage()}>
                    Open the full play
                  </button>
                  <button className="cc-cs-btn cc-cs-btn--ghost"
                    onClick={() => onNavigate && onNavigate('floor', null, { type: 'account', accountId: centerStage.account_id, accountName: centerStage.account_name })}>
                    To The Floor
                  </button>
                </div>
              </div>
            ) : !theMoment ? (
              <div className="cc-grid-empty">
                <div className="cc-grid-empty-icon">🌅</div>
                <div className="cc-grid-empty-text">Jarvis selects your daily focus account each morning. Check back after 6 AM.</div>
              </div>
            ) : theMoment.isCompleted ? (
              <div className="cc-grid-empty">
                <div className="cc-grid-empty-icon">✅</div>
                <div className="cc-grid-empty-text">Today's focus account addressed. Strong work.</div>
              </div>
            ) : (
              <div className="cc-moment-inline">
                <div className="cc-moment-account-name">{theMoment.accountName}</div>
                {theMoment.opportunities?.map(o => (
                  <div key={o.id} className="cc-moment-opp">
                    <span className="cc-moment-opp-name">{o.name}</span>
                    {o.probability && <span className="cc-moment-opp-stage">{o.probability.replace(/^\d+%\s*-\s*/, '')}</span>}
                    {o.meddicCompleteness > 0 && <span className="cc-moment-opp-meddic">MEDDIC {o.meddicCompleteness}%</span>}
                  </div>
                ))}
                {theMoment.narrative && (
                  <div className="cc-moment-narrative">{theMoment.narrative}</div>
                )}
                <div className="cc-moment-decision-prompt">What are you doing with this today?</div>
                <div className="cc-moment-actions cc-moment-actions--decision">
                  <button className="cc-moment-btn cc-moment-btn--execute"
                    title="Execute — open in The Floor and engage now"
                    onClick={() => {
                      if (theMoment.id) apiFetch('/api/jarvis/the-moment', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'complete', momentId: theMoment.id }) }).catch(() => {});
                      onNavigate && onNavigate('floor', null, { type: 'account', accountId: theMoment.accountId, accountName: theMoment.accountName });
                    }}>
                    ✅ Execute
                  </button>
                  <button className="cc-moment-btn cc-moment-btn--modify"
                    title="Modify — open in The Floor and ask Victor to adjust the approach"
                    onClick={() => {
                      if (theMoment.id) apiFetch('/api/jarvis/the-moment', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'modify', momentId: theMoment.id }) }).catch(() => {});
                      onNavigate && onNavigate('floor', null, {
                        type: 'account', accountId: theMoment.accountId, accountName: theMoment.accountName,
                        autoMessage: `Victor, I want to revisit the approach for ${theMoment.accountName}. The current angle is: "${theMoment.narrative}" — what adjustments would you recommend?`,
                      });
                    }}>
                    ✏️ Modify
                  </button>
                  <button className="cc-moment-btn cc-moment-btn--reject"
                    title="Reject — not the right focus today, show me the next account"
                    onClick={async () => {
                      if (theMoment.id) {
                        const res = await apiFetch('/api/jarvis/the-moment', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'dismiss', momentId: theMoment.id }) }).then(r => r.json()).catch(() => null);
                        if (res?.moment && typeof window !== 'undefined') window.location.reload();
                      }
                    }}>
                    ❌ Reject
                  </button>
                  <button className="cc-moment-btn cc-moment-btn--defer"
                    title="Defer — not today, remind me tomorrow"
                    onClick={() => {
                      if (theMoment.id) apiFetch('/api/jarvis/the-moment', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'defer', momentId: theMoment.id }) }).catch(() => {});
                      // Clear the moment from the UI
                      if (typeof window !== 'undefined') window.location.reload();
                    }}>
                    ⏸ Defer
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Q2 — People to Engage (top-right) */}
        <div className="cc-grid-cell cc-grid-cell--people">
          <div className="cc-grid-cell-header">
            <div className="cc-grid-cell-icon cc-grid-icon--purple">🔥</div>
            <div className="cc-grid-cell-title">People to Engage{(engagePeople || []).length > 0 ? ` · ${engagePeople.length}` : ''}</div>
            <button className="cc-grid-cell-link" onClick={() => onNavigate && onNavigate('contacts')}>
              Contacts →
            </button>
          </div>
          <div className="cc-grid-cell-body" style={{ maxHeight: 380, overflowY: 'auto' }}>
            {(engagePeople || []).length === 0 ? (
              <div className="cc-grid-empty">
                <div className="cc-grid-empty-icon">👥</div>
                <div className="cc-grid-empty-text">All relationships current — no overdue touchpoints</div>
              </div>
            ) : (
              (engagePeople || []).map((person) => {
                const initials = (person.contactName || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                const colors = ['cc-av-blue', 'cc-av-purple', 'cc-av-cyan', 'cc-av-indigo', 'cc-av-teal'];
                const colorClass = colors[(person.contactName || '').charCodeAt(0) % colors.length];
                const confColor = { High: '#16A34A', Medium: 'var(--dm-amber)', Low: 'var(--dm-red)' }[person.confidence] || 'var(--dm-grey-600)';
                return (
                  <div key={person.contactId} className="cc-grid-person-item"
                    onClick={() => onSelectPerson && onSelectPerson(person)}
                    style={{ cursor: 'pointer' }}>
                    <div className={`cc-grid-person-avatar ${colorClass}`}>{initials}</div>
                    <div className="cc-grid-person-body">
                      <div className="cc-grid-person-name-row">
                        <span className="cc-grid-person-name">{person.contactName}</span>
                        {person.title && <span className="cc-grid-person-title">{person.title}</span>}
                        {person.accountName && <span className="cc-grid-person-title" style={{ opacity: 0.65 }}>· {person.accountName}</span>}
                      </div>
                      {person.whyNow && (
                        <div className="cc-grid-person-field-value" style={{ margin: '3px 0 5px', lineHeight: 1.35 }}>{person.whyNow}</div>
                      )}
                      <div className="cc-grid-person-footer">
                        {person.roleInDeal && (
                          <span className="cc-grid-person-confidence" style={{ color: 'var(--dm-grey-600)' }}>{person.roleInDeal}</span>
                        )}
                        {person.confidence && (
                          <span className="cc-grid-person-confidence" style={{ color: confColor }}>● {person.confidence} confidence</span>
                        )}
                      </div>
                    </div>
                    <div className="cc-grid-person-act" title="Open engagement panel">→</div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Q3 — Top Opportunities (bottom-left) */}
        <div className="cc-grid-cell cc-grid-cell--opps">
          <div className="cc-grid-cell-header">
            <div className="cc-grid-cell-icon cc-grid-icon--green">🔥</div>
            <div className="cc-grid-cell-title">Top Opportunities</div>
            <button className="cc-grid-cell-link" onClick={() => onNavigate && onNavigate('pipeline')}>
              Pipeline →
            </button>
          </div>
          <div className="cc-grid-cell-body">
            {topOpps.length === 0 ? (
              <div className="cc-grid-empty">
                <div className="cc-grid-empty-icon">📊</div>
                <div className="cc-grid-empty-text">No active opportunities — add accounts and create opportunities to track here</div>
              </div>
            ) : (
              topOpps.map((opp, idx) => (
                <div key={opp.id} className="cc-grid-opp-item"
                  onClick={() => opp.account_id && onNavigate && onNavigate('account-detail', opp.account_id, { openOpportunityId: opp.id })}
                  style={{ cursor: opp.account_id ? 'pointer' : 'default' }}>
                  <div className="cc-grid-opp-rank">{idx + 1}</div>
                  <div className="cc-grid-opp-body">
                    <div className="cc-grid-opp-name">{opp.name}</div>
                    <div className="cc-grid-opp-meta">
                      {opp.accountName && <span className="cc-grid-opp-account">{opp.accountName}</span>}
                      {opp.probability && <span className="cc-grid-opp-stage">{opp.probability.replace(/^\d+%\s*-\s*/, '')}</span>}
                      {opp.meddicCompleteness > 0 && (
                        <span className={`cc-grid-opp-meddic ${opp.meddicCompleteness >= 60 ? 'cc-meddic-ok' : opp.meddicCompleteness >= 30 ? 'cc-meddic-warn' : 'cc-meddic-low'}`}>
                          MEDDIC {opp.meddicCompleteness}%
                        </span>
                      )}
                      {opp._daysToClose !== undefined && opp._daysToClose <= 90 && (
                        <span className="cc-grid-opp-close">
                          {opp._daysToClose <= 0 ? '⚠️ Overdue' : `${opp._daysToClose}d`}
                        </span>
                      )}
                    </div>
                    {opp.recommendedNextAction ? (
                      <div className="cc-grid-opp-next-action">
                        <span className="cc-opp-next-label">Next:</span> {opp.recommendedNextAction}
                      </div>
                    ) : (
                      <div className="cc-grid-opp-next-action cc-opp-next-generating">
                        No next action set
                      </div>
                    )}
                  </div>
                  <div className="cc-grid-opp-value">
                    {getDealValue(opp.deal_size) > 0 ? formatCurrency(getDealValue(opp.deal_size)) : '—'}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Q4 — At-Risk Deals (bottom-right) */}
        <div className="cc-grid-cell cc-grid-cell--atrisk">
          <div className="cc-grid-cell-header">
            <div className="cc-grid-cell-icon cc-grid-icon--red">⚠️</div>
            <div className="cc-grid-cell-title">At-Risk Deals</div>
            {atRisk.length > 0 && (
              <span className="cc-grid-cell-count cc-grid-cell-count--red">{atRisk.length} flagged</span>
            )}
          </div>
          <div className="cc-grid-cell-body">
            {atRisk.length === 0 ? (
              <div className="cc-grid-empty">
                <div className="cc-grid-empty-icon">🟢</div>
                <div className="cc-grid-empty-text">No deals at risk — pipeline is healthy</div>
              </div>
            ) : (
              atRisk.map((opp) => {
                // Compute next best action based on risk reason and opportunity context
                const champion = opp.meddicChampion || opp.meddic_champion || '';
                const economicBuyer = opp.meddicEconomicBuyer || opp.meddic_economic_buyer || '';
                const contactName = champion || economicBuyer || (opp.accountName ? opp.accountName + ' executive' : 'key contact');
                let nextAction = '';
                const risk = (opp.riskReason || '').toLowerCase();
                if (risk.includes('no activity') || risk.includes('stall') || risk.includes('silent')) {
                  nextAction = `Email ${contactName} — re-engage and check on timeline`;
                } else if (risk.includes('meddic') || risk.includes('qualification') || risk.includes('no economic buyer')) {
                  nextAction = `Request meeting with ${contactName} to confirm decision process`;
                } else if (risk.includes('close') || risk.includes('overdue') || risk.includes('past')) {
                  nextAction = `Call ${contactName} — close date at risk, get updated timeline`;
                } else if (risk.includes('competitor') || risk.includes('competitive')) {
                  nextAction = `Brief ${contactName} on DigitalMe differentiation — competitive threat active`;
                } else {
                  nextAction = `Engage ${contactName} — review opportunity status`;
                }
                return (
                  <div key={opp.id} className="cc-grid-risk-item"
                    onClick={() => opp.account_id && onNavigate && onNavigate('account-detail', opp.account_id, { openOpportunityId: opp.id })}
                    style={{ cursor: opp.account_id ? 'pointer' : 'default' }}>
                    <div className="cc-grid-risk-indicator" />
                    <div className="cc-grid-risk-body">
                      <div className="cc-grid-risk-name">{opp.name}</div>
                      <div className="cc-grid-risk-meta">
                        {opp.accountName && <span className="cc-grid-risk-account">{opp.accountName}</span>}
                        <span className="cc-grid-risk-reason">{opp.riskReason}</span>
                      </div>
                      <div className="cc-grid-risk-action">→ {nextAction}</div>
                    </div>
                    <button className="cc-grid-risk-act"
                      onClick={e => {
                        e.stopPropagation();
                        onNavigate && onNavigate('floor', null, {
                          type: 'alert',
                          accountId: opp.account_id,
                          accountName: opp.accountName,
                          action: `Review ${opp.name} — ${opp.riskReason}. Next step: ${nextAction}`,
                        });
                      }}>
                      ⚡
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>

      </div>{/* /cc-grid-2x2 */}
    </div>
  );
}


// -- News Item -------------------------------------------------
// -- Story Popup Modal ----------------------------------------
function NewsActionPanel({ item, onClose, onNavigate }) {
  return (
    <div className="cc-modal-overlay" onClick={onClose}>
      <div className="cc-news-action-panel" onClick={e => e.stopPropagation()}>
        <button className="cc-modal-close" onClick={onClose}>✕</button>
        <div className="cc-news-action-source">{item.source} · {item.time}</div>
        <p className="cc-news-action-headline">{item.headline}</p>
        {item.relevanceNote && <p className="cc-news-action-note">💡 {item.relevanceNote}</p>}
        {item.summary && <p className="cc-news-action-summary">{item.summary}</p>}
        <div className="cc-news-action-buttons">
          {item.url && (
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="cc-news-action-btn cc-news-action-btn--read"
            >📰 Read Article ↗</a>
          )}
          <button
            className="cc-news-action-btn cc-news-action-btn--research"
            onClick={() => { onClose(); onNavigate && onNavigate('floor', null, { type: 'nav', message: `Selma, research this signal and build an intelligence package. Find any companies in our market affected by this: "${item.headline}". For each one you find, check if they already exist in our accounts. If not, note them as potential new accounts to add. Return: key findings, affected companies, and any buying signals this creates.` }); }}
          >🔍 Selma — Research</button>
          <button
            className="cc-news-action-btn cc-news-action-btn--strategy"
            onClick={() => { onClose(); onNavigate && onNavigate('floor', null, { type: 'nav', message: `I just saw this signal: "${item.headline}" — what does this mean for our pipeline and how should we respond?` }); }}
          >⚡ Ask Victor</button>
          <button
            className="cc-news-action-btn cc-news-action-btn--outreach"
            onClick={() => { onClose(); onNavigate && onNavigate('floor', null, { type: 'nav', message: `Cyrano, draft a Signal-Led outreach email using this industry signal: "${item.headline}"` }); }}
          >✍️ Draft Outreach</button>
          <button
            className="cc-news-action-btn cc-news-action-btn--task"
            onClick={async () => {
              try {
                await apiFetch('/api/tasks/create', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    name: `Follow up on signal: ${item.headline}`,
                    status: 'Suggested',
                    priority: 'Do Today',
                    notes: item.relevanceNote || item.summary || '',
                    autoGenerated: true,
                  }),
                });
              } catch (e) { console.error('Task create failed', e); }
              onClose();
            }}
          >✅ Create Task</button>
        </div>
      </div>
    </div>
  );
}

function StoryModal({ item, type, onClose }) {
  if (!item) return null;
  const isNews = type === 'news';
  return (
    <div className="cc-modal-overlay" onClick={onClose}>
      <div className="cc-modal" onClick={e => e.stopPropagation()}>
        <div className="cc-modal-header">
          <span className="cc-modal-type-badge">{isNews ? '📰 Industry News' : `🏁 ${item.type || 'Intel'}`}</span>
          <button className="cc-modal-close" onClick={onClose}>✕</button>
        </div>
        {isNews ? (
          <>
            <div className="cc-modal-source-row">
              <strong>{item.source}</strong>
              <span className="cc-modal-time">{item.time}</span>
            </div>
            <h3 className="cc-modal-headline">{item.headline}</h3>
            <div className="cc-modal-relevance">
              <span className={`cc-rel-dot cc-rel-${item.relevance}`} style={{display:'inline-block',marginRight:6}} />
              {item.relevanceNote}
            </div>
            {item.url && (
              <a href={item.url} target="_blank" rel="noopener noreferrer" className="cc-modal-link">
                Read full article →
              </a>
            )}
          </>
        ) : (
          <>
            <div className="cc-modal-source-row">
              <strong>{item.entity}</strong>
              <span className={`cc-intel-badge cc-intel-badge--${item.color}`}>{item.type}</span>
              <span className="cc-modal-time">{item.time}</span>
            </div>
            <p className="cc-modal-intel-text">{item.text}</p>
          </>
        )}
        <div className="cc-modal-actions">
          <button className="cc-modal-action-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// -- Read tracking helpers ------------------------------------
function getReadStories() {
  try {
    const data    = JSON.parse(localStorage.getItem('dm_read_stories') || '{}');
    const cutoff  = Date.now() - 7 * 24 * 60 * 60 * 1000; // 7 day expiry
    return Object.fromEntries(Object.entries(data).filter(([, ts]) => ts > cutoff));
  } catch { return {}; }
}
function markStoryRead(url) {
  try {
    const data = getReadStories();
    data[url]  = Date.now();
    localStorage.setItem('dm_read_stories', JSON.stringify(data));
  } catch {}
}

function NewsItem({ item, onClick, onMarkRead }) {
  const readMap  = getReadStories();
  const isRead   = !!(item.url && readMap[item.url]);

  const srcClass = {
    'Insurance Journal': 'cc-src-ij',
    'AM Best':           'cc-src-amb',
    'Carrier Management':'cc-src-cm',
    'InsureTech Connect':'cc-src-itc',
    'Business Insurance':'cc-src-bi',
  }[item.source] || 'cc-src-ij';

  const relClass = { high: 'cc-rel-high', med: 'cc-rel-med', low: 'cc-rel-low' }[item.relevance] || 'cc-rel-low';

  const handleClick = () => {
    if (item.url) markStoryRead(item.url);
    if (onMarkRead) onMarkRead(item.url);
    if (onClick) onClick(item);
  };

  return (
    <div
      className={`cc-news-item cc-news-item--clickable ${isRead ? 'cc-news-item--read' : 'cc-news-item--unread'}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && handleClick()}
    >
      <div className="cc-news-source-row">
        {!isRead && <span className="cc-news-unread-dot" />}
        <span className={`cc-news-source ${srcClass}`}>{item.source}</span>
        <span className="cc-news-time">{item.time}</span>
        {item.isStatic && <span className="cc-news-static-badge">Evergreen</span>}
        {item.url && <span className="cc-news-external-icon">↗</span>}
      </div>
      <div className="cc-news-headline">{item.headline}</div>
      <div className="cc-news-relevance">
        <div className={`cc-rel-dot ${relClass}`} />
        {item.relevanceNote}
      </div>
    </div>
  );
}




// ================================================================
// MAIN CommandCenter
// ================================================================
export default function CommandCenter({ onNavigate, badges }) {

  const [isLoading, setIsLoading]           = useState(true);
  const [selectedTask, setSelectedTask]     = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [allAccounts, setAllAccounts]         = useState([]);
  const [allOpportunities, setAllOpportunities] = useState([]);
  const [storyModal, setStoryModal]         = useState(null);
  const [newsAction, setNewsAction]         = useState(null);

  // ── The Moment state ────────────────────────────────────────────────────────
  const [theMoment, setTheMoment] = useState(null);
  const [momentLoading, setMomentLoading] = useState(true);

  // ── Center Stage state (supersedes The Moment when a brief exists) ───────────
  const [centerStage, setCenterStage] = useState(null);
  const [csOpen, setCsOpen] = useState(false);
  // Past Findings library
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryItems, setLibraryItems] = useState(null);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryBrief, setLibraryBrief] = useState(null);
  const openLibrary = () => {
    setLibraryOpen(true);
    setLibraryLoading(true);
    apiFetch('/api/jarvis/center-stage?view=history&limit=20')
      .then(r => r.json())
      .then(d => setLibraryItems(d && d.success ? (d.history || []) : []))
      .catch(() => setLibraryItems([]))
      .finally(() => setLibraryLoading(false));
  };

  // ── Victor Action Queue state ───────────────────────────────────────────
  const [queueItems,    setQueueItems]    = useState([]);
  // A failed queue mutation used to reach only console.error — the click looked
  // like it did nothing. This surfaces it (P1.5 IA-8a).
  const [queueError,    setQueueError]    = useState(null);
  const [completingId,  setCompletingId]  = useState(null);
  const [engagePeople,  setEngagePeople]  = useState([]);
  const [overnightItems, setOvernightItems] = useState([]);
  const [pipelineOpps,   setPipelineOpps]   = useState([]);
  const [coldAccounts,   setColdAccounts]   = useState([]);
  const [briefText,    setBriefText]    = useState('');
  const [briefLoading, setBriefLoading] = useState(false);
  const [openMenu,     setOpenMenu]     = useState(null); // 'actions' | 'manage' | null

  // User amend actions on an engagement item (done / dismiss / snooze).
  // Optimistically drop it from the list, then POST; the nightly regen reconciles.
  const handleEngageAction = async (action, item, extra = {}) => {
    if (!item || !item.id) return;
    setEngagePeople(prev => (prev || []).filter(p => p.id !== item.id));
    setSelectedPerson(null);
    try {
      await apiFetch('/api/engage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, id: item.id, ...extra }),
      });
    } catch {
      /* non-fatal — the item reappears on the next generation if the write failed */
    }
  };

  // Fetch Victor's tailored brief whenever a person is opened (cached server-side).
  // Keyed on id (not the object) so a background list refresh can't cancel an in-flight fetch.
  useEffect(() => {
    const pid = selectedPerson?.id;
    if (!pid) { setBriefText(''); setOpenMenu(null); return; }
    let cancelled = false;
    setBriefText(''); setBriefLoading(true); setOpenMenu(null);
    apiFetch('/api/engage/brief', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: pid }),
    }).then(r => r.json()).then(d => { if (!cancelled) setBriefText(d.brief || ''); })
      .catch(() => {}).finally(() => { if (!cancelled) setBriefLoading(false); });
    return () => { cancelled = true; };
  }, [selectedPerson?.id]);

  // Turn an engagement item into a tracked task.
  const handleCreateTask = async (item) => {
    if (!item) return;
    setOpenMenu(null); setSelectedPerson(null);
    try {
      await apiFetch('/api/tasks/create', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `Engage ${item.contactName} at ${item.accountName}`,
          accountId: item.accountId, opportunityId: item.opportunityId || null,
          taskType: 'Engagement', priority: 'Medium', status: 'To Do',
          notes: `Why now: ${item.whyNow || ''}\nAngle: ${item.bestAngle || ''}`,
          generatedBy: 'people-to-engage',
        }),
      });
    } catch {
      /* non-fatal */
    }
  };

  // Split Victor's brief into its labelled parts so WHY NOW isn't shown twice
  // (once as the signal headline, once inside the brief).
  const parseBrief = (text) => {
    if (!text) return null;
    const out = { whyNow: '', angle: '', opener: '' };
    const labels = [['WHY NOW', 'whyNow'], ['ANGLE', 'angle'], ['OPENER', 'opener']];
    labels.forEach(([label, key], i) => {
      const marker = `**${label}:**`;
      const start = text.indexOf(marker);
      if (start === -1) return;
      const from = start + marker.length;
      let end = text.length;
      for (let j = i + 1; j < labels.length; j++) {
        const ns = text.indexOf(`**${labels[j][0]}:**`, from);
        if (ns !== -1) { end = ns; break; }
      }
      out[key] = text.slice(from, end).trim();
    });
    return out;
  };
  const parsedBrief = parseBrief(briefText);

  useEffect(() => {
    // Fire all fast data calls in parallel immediately
    Promise.all([
      apiFetch('/api/victor/situation-queue').then(r => r.json()).catch(() => ({})),
      apiFetch('/api/opportunities/list').then(r => r.json()).catch(() => ({})),
      apiFetch('/api/accounts/list').then(r => r.json()).catch(() => ({})),
      apiFetch('/api/engage').then(r => r.json()).catch(() => ({})),
      apiFetch('/api/jarvis/overnight-feed').then(r => r.json()).catch(() => ({})),
      apiFetch('/api/accounts/cold').then(r => r.json()).catch(() => ({})),
    ]).then(([queueData, oppsData, acctData, peopleData, overnightData, coldData]) => {
      // Queue
      if (queueData.items) setQueueItems(queueData.items);

      // Opportunities
      if (oppsData.success && oppsData.opportunities?.length > 0) {
        setAllOpportunities(oppsData.opportunities);
        // Pipeline Pulse — sort by victor_confidence (Low first = most urgent) then deal size
        const confOrder = { Low: 0, Medium: 1, High: 2 };
        const pulseOpps = [...oppsData.opportunities]
          .filter(o => o.probability && !['Closed Won', 'Closed Lost', 'Tabled'].includes(o.status))
          .sort((a, b) => (confOrder[a.victor_confidence] ?? 1) - (confOrder[b.victor_confidence] ?? 1)
            || (b.deal_size || b.dealSize || 0) - (a.deal_size || a.dealSize || 0))
          .slice(0, 4);
        setPipelineOpps(pulseOpps);
      }

      // Accounts / heat map
      if (acctData.success && acctData.accounts?.length > 0) {
        const hm = acctData.accounts.map(a => {
          const lastUp = a.lastAiUpdate ? new Date(a.lastAiUpdate) : null;
          const diffDays = lastUp ? Math.floor((Date.now() - lastUp.getTime()) / 86400000) : 999;
          let heat = 'cold';
          const sigs = a.aiSignals || [];
          if (sigs.includes('New Executive') || sigs.includes('Budget Allocated')) heat = 'hot';
          else if (diffDays === 0) heat = 'hot';
          else if (diffDays <= 7)  heat = 'warm';
          else if (diffDays <= 21) heat = 'active';
          else if (diffDays <= 60) heat = 'new';
          const daysLabel = diffDays === 0 ? 'Today' : diffDays === 999 ? 'Never' : diffDays > 90 ? '90d+' : `${diffDays}d`;
          return {
            id: a.id, name: a.accountName, heat, days: daysLabel,
            tier: (a.tier || '').replace('Tier ', '').replace('tier ', ''),
            status: a.accountStatus, signalCount: a.signalCount || 0,
            isFocus: false,
            lastContactDate: a.lastContactDate, lastContact: a.lastContact,
            contactCadence: a.contactCadence, accountName: a.accountName,
          };
        });
        hm.sort((a, b) => {
          const heatOrder = { hot: 0, warm: 1, active: 2, new: 3, cold: 4 };
          return (heatOrder[a.heat] || 4) - (heatOrder[b.heat] || 4) || (a.tier || '9').localeCompare(b.tier || '9');
        });
        setAllAccounts(hm);
      }

      // People to Engage — Victor-generated engagement queue (/api/engage)
      if (peopleData.items?.length > 0) {
        setEngagePeople(peopleData.items);
      }

      // Overnight Intelligence Feed
      if (overnightData.items?.length > 0) setOvernightItems(overnightData.items);

      // Cold Accounts
      if (coldData.accounts?.length > 0) setColdAccounts(coldData.accounts);

      setIsLoading(false);
    });

    // The Moment loads separately — it's AI-generated and slightly slower
    apiFetch('/api/jarvis/the-moment')
      .then(r => r.json())
      .then(d => { if (d.moment) setTheMoment(d.moment); })
      .catch(() => {})
      .finally(() => setMomentLoading(false));

    // Center Stage — the pre-cooked daily play; supersedes The Moment when present
    apiFetch('/api/jarvis/center-stage')
      .then(r => r.json())
      .then(d => { if (d && d.success && d.brief) setCenterStage(d.brief); })
      .catch(() => {});

    // Industry news loads last — lowest priority
    apiFetch('/api/intel/news')
      .then(r => r.json())
      .then(d => { if (d.success && d.items?.length > 0) setNewsItems(d.items); })
      .catch(() => {})
      .finally(() => setNewsLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Acts on a queue item: marks Actioned + opens The Floor with full context
  // Identical pattern to Priority Alert onAct — body click opens floor, logs as processed
  const handleActQueue = async (item) => {
    // Was non-blocking with the Floor opening regardless, which meant a failed
    // write happened on a screen the user had already left (P1.5 IA-8a).
    let items;
    try {
      ({ items } = await completeQueueItem(item.id));
    } catch (err) {
      setQueueError(`Could not mark "${item.action}" as actioned — ${err.message}`);
      return;
    }
    setQueueError(null);
    // The response carries the refreshed list, which a local filter cannot
    // reproduce: `complete` auto-generates a replacement when fewer than three
    // items remain (situation-queue.js:230-241).
    if (items) setQueueItems(items);
    else setQueueItems(prev => prev.filter(q => q.id !== item.id));

    // Open The Floor with the item's context — mirrors Priority Alert Act pattern
    onNavigate && onNavigate('floor', null, {
      type:        'queue',
      action:      item.action,
      accountId:   item.accountId   || undefined,
      accountName: item.accountName || undefined,
      preAnalysis: item.why         || '',
      returnTo:    'dashboard',
    });
  };

  // These three previously reported failure with console.error, which the user
  // never sees: the click appeared to do nothing and the item stayed put with
  // no explanation. Same write, same rule, one helper (P1.5 IA-8a).
  const runQueueMutation = async (fn, failureMessage, onStart, onEnd) => {
    if (onStart) onStart();
    try {
      const { items } = await fn();
      if (items) setQueueItems(items);
      setQueueError(null);
    } catch (err) {
      setQueueError(`${failureMessage} — ${err.message}`);
    } finally {
      if (onEnd) onEnd();
    }
  };

  const handleCompleteQueue = (itemId) => runQueueMutation(
    () => completeQueueItem(itemId),
    'Could not complete that item',
    () => setCompletingId(itemId),
    () => setCompletingId(null),
  );

  const handleSnoozeQueue = (itemId) => runQueueMutation(
    () => snoozeQueueItem(itemId, 1),
    'Could not snooze that item',
  );

  const handleDismissQueue = (itemId) => runQueueMutation(
    () => dismissQueueItem(itemId),
    'Could not dismiss that item',
  );

  // Static placeholder data -- Sprint 2 will replace with live feed (real article URLs per story)
  const [newsItems, setNewsItems]     = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);





  // Tasks: show from API only — no hardcoded fallback
  // If empty after load, show a clean empty state




  return (
    <>
    <div className="cc-root">

      {/* -- Page Body -- */}
      <div className="cc-body">

        {/* ── The Moment — daily account focus card ──────────────────────────
            Hidden whenever the Center Stage cell already shows this moment.
            That cell renders the brief when centerStage exists and otherwise
            falls back to cc-moment-inline (:443), so a !centerStage guard
            alone rendered the moment twice — the standalone card here and the
            inline copy in the grid. Gate on theMoment too, leaving this card
            for the one state the grid does not cover. ── */}
        {!centerStage && !theMoment && (
          <TheMoment
            moment={theMoment}
            loading={momentLoading}
            onNavigate={onNavigate}
            onComplete={(m) => setTheMoment({ ...m, isCompleted: true })}
            onDismiss={(newMoment) => setTheMoment(newMoment)}
          />
        )}

        {queueError && (
          <div className="cc-action-error" role="alert">
            ⚠️ {queueError}
            <button className="cc-action-error-dismiss"
                    onClick={() => setQueueError(null)}
                    aria-label="Dismiss message">×</button>
          </div>
        )}

        {/* ── 2x2 Command Center (greeting + grid) ───────────────────────── */}
        <CommandGrid
          queueItems={queueItems}
          opportunities={allOpportunities}
          accounts={allAccounts}
          engagePeople={engagePeople}
          overnightItems={overnightItems}
          pipelineOpps={pipelineOpps}
          theMoment={theMoment}
          centerStage={centerStage}
          onOpenCenterStage={() => setCsOpen(true)}
          onOpenLibrary={openLibrary}
          onNavigate={onNavigate}
          onCompleteQueue={handleCompleteQueue}
          onSnoozeQueue={handleSnoozeQueue}
          onDismissQueue={handleDismissQueue}
          onActQueue={handleActQueue}
          completingId={completingId}
          onSelectPerson={setSelectedPerson}
        />


        {/* Bottom: two-col — left stack (Overnight + Industry + Cold) | right (Pipeline Pulse) */}
        <div className="cc-bottom-layout cc-fade-in cc-s2">

          {/* Left column — stacked cards */}
          <div className="cc-bottom-left">

            {/* Overnight Intelligence */}
            <Card>
              <CardHeader icon="🌙" iconClass="cc-icon-indigo" title="Overnight Intelligence"
                action="Today →" onAction={() => onNavigate && onNavigate('today')}
              />
              <div className="cc-card-body">
                {overnightItems.length === 0 ? (
                  <div className="cc-empty-state">
                    {isLoading ? 'Loading overnight activity…' : 'The team is running overnight research — check back after 6 AM.'}
                  </div>
                ) : (
                  overnightItems.map((item, i) => (
                    <div key={i} className="cc-overnight-item"
                      onClick={() => onNavigate && onNavigate(item.route, null, item.routeParams)}
                      style={{ cursor: 'pointer' }}>
                      <span className="cc-overnight-icon">{item.icon}</span>
                      <div className="cc-overnight-body">
                        <div className="cc-overnight-text">{item.text}</div>
                        <div className="cc-overnight-action">{item.action} →</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>

            {/* Industry Intelligence */}
            <Card>
              <CardHeader icon="📰" iconClass="cc-icon-cyan" title="Industry Intelligence"
                action={newsItems.length > 0 ? `${newsItems.filter(n => !getReadStories()[n.url]).length} new` : null}
              />
              <div className="cc-card-body cc-scroll">
                {newsLoading ? (
                  <div className="cc-empty-state">Scanning industry sources…</div>
                ) : newsItems.length === 0 ? (
                  // B74: the feed used to fall back to three hardcoded P&C
                  // headlines, so it was never empty and never said why. It can
                  // now genuinely be empty — most often because the tenant has
                  // not said what it sells or which market it sells into, which
                  // is what the searches are built from. Naming that is more
                  // use than "check back soon".
                  <div className="cc-empty-state">
                    No recent news found. If this stays empty, check that your
                    services and market are set in Profile &amp; Settings → Company —
                    the news search is built from them.
                  </div>
                ) : (
                  newsItems.map((item, i) => (
                    <NewsItem key={i} item={item}
                      onClick={item => setNewsAction({ item })}
                      onMarkRead={() => setNewsItems(prev => [...prev])} />
                  ))
                )}
              </div>
            </Card>

            {/* Cold Accounts moved to below Pipeline Pulse */}

          </div>{/* /cc-bottom-left */}

          {/* Right column — Pipeline Pulse full height */}
          <div className="cc-bottom-right">
            <Card>
              {/* Pipeline Pulse — gauge in header */}
              <div className="pp-header">
                <span className="pp-title">Pipeline Pulse</span>
                {(() => {
                  const total = pipelineOpps.reduce((s, o) => s + (o.deal_size || o.dealSize || 0), 0);
                  const weights = pipelineOpps.map(o => ({ conf: o.victor_confidence || 'Low', size: o.deal_size || o.dealSize || 1 }));
                  const totalW = weights.reduce((s, w) => s + w.size, 0) || 1;
                  const score = weights.reduce((s, w) => s + (w.conf === 'High' ? 2 : w.conf === 'Medium' ? 1 : 0) * w.size, 0) / totalW;
                  const healthPct = totalW > 0 ? score / 2 : 0;
                  const healthLabel = healthPct > 0.65 ? 'strong' : healthPct > 0.35 ? 'moderate' : 'at risk';
                  const fmt = (v) => v >= 1000000 ? '$' + (v/1000000).toFixed(1) + 'M' : v >= 1000 ? '$' + (v/1000).toFixed(0) + 'K' : '$' + v;
                  const angle = 180 - (healthPct * 180);
                  const rad = angle * Math.PI / 180;
                  const nx = 22 + 16 * Math.cos(Math.PI - rad);
                  const ny = 24 - 16 * Math.sin(Math.PI - rad);
                  return (
                    <div className="pp-gauge-btn" onClick={() => onNavigate && onNavigate('pipeline')} title="View full pipeline">
                      <svg width="44" height="26" viewBox="0 0 44 26" role="img" aria-label="Pipeline health gauge">
                        <path d="M 4 24 A 18 18 0 0 1 40 24" fill="none" stroke="var(--color-border-tertiary)" strokeWidth="5" strokeLinecap="butt"/>
                        <path d="M 4 24 A 18 18 0 0 1 14.6 6.8" fill="none" stroke="#F09595" strokeWidth="5" strokeLinecap="butt"/>
                        <path d="M 14.6 6.8 A 18 18 0 0 1 29.4 6.8" fill="none" stroke="#FAC775" strokeWidth="5" strokeLinecap="butt"/>
                        <path d="M 29.4 6.8 A 18 18 0 0 1 40 24" fill="none" stroke="#C0DD97" strokeWidth="5" strokeLinecap="butt"/>
                        <line x1="22" y1="24" x2={nx.toFixed(1)} y2={ny.toFixed(1)} stroke="var(--color-text-primary)" strokeWidth="1.5" strokeLinecap="round"/>
                        <circle cx="22" cy="24" r="2.5" fill="var(--color-text-primary)"/>
                      </svg>
                      <div className="pp-gauge-text">
                        <span className="pp-gauge-total">{total > 0 ? fmt(total) : '--'}</span>
                        <span className="pp-gauge-status">{healthLabel} · pipeline →</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="cc-card-body cc-scroll">
                {isLoading ? (
                  <div className="cc-empty-state">Loading pipeline…</div>
                ) : pipelineOpps.length === 0 ? (
                  <div className="cc-empty-state">No active opportunities — add deals to see pipeline health.</div>
                ) : (() => {
                  const maxSize = Math.max(...pipelineOpps.map(o => o.deal_size || o.dealSize || 0), 1);
                  return pipelineOpps.slice(0, 6).map((opp) => {
                    const conf = opp.victor_confidence || 'Low';
                    const barColor   = conf === 'High' ? '#639922' : conf === 'Medium' ? '#BA7517' : '#A32D2D';
                    const dotColor   = conf === 'High' ? 'pp-dot-green' : conf === 'Medium' ? 'pp-dot-yellow' : 'pp-dot-red';
                    const actionColor = conf === 'High' ? 'pp-action-green' : conf === 'Medium' ? 'pp-action-yellow' : 'pp-action-red';
                    const stageLabel = (opp.stage || opp.probability || '').split(' - ')[1] || '';
                    const size = opp.deal_size || opp.dealSize || 0;
                    const barPct = Math.round((size / maxSize) * 100);
                    const fmt = (v) => v >= 1000000 ? '$' + (v/1000000).toFixed(1) + 'M' : v >= 1000 ? '$' + (v/1000).toFixed(0) + 'K' : '$' + v;
                    return (
                      <div key={opp.id} className="cc-pulse-item"
                        onClick={() => opp.account_id && onNavigate && onNavigate('account-detail', opp.account_id, { openOpportunityId: opp.id })}
                        style={{ cursor: 'pointer' }}>
                        <div className="pp-deal-top">
                          <div className={`pp-dot ${dotColor}`} />
                          <span className="pp-deal-name">{opp.name || opp.opportunityName}</span>
                          <span className="pp-deal-value">{size > 0 ? fmt(size) : ''}</span>
                        </div>
                        <div className="pp-bar-wrap">
                          <div className="pp-bar" style={{ width: barPct + '%', background: barColor }} />
                        </div>
                        <div className="pp-stage">{stageLabel}</div>
                        <div className={`pp-action ${actionColor}`}>{opp.victor_read || 'Awaiting Victor assessment'}</div>
                      </div>
                    );
                  });
                })()}
              </div>
            </Card>

            {/* Cold Accounts — below Pipeline Pulse */}
            <Card>
              <CardHeader icon="🥶" iconClass="cc-icon-blue" title="Cold Accounts"
                action="Accounts →" onAction={() => onNavigate && onNavigate('accounts')}
              />
              <div className="cc-card-body">
                {coldAccounts.length === 0 ? (
                  <div className="cc-empty-state">
                    {isLoading ? 'Loading…' : 'All accounts are current — no overdue relationships with active signals.'}
                  </div>
                ) : (
                  coldAccounts.map((acct) => {
                    const initials = (acct.accountName || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
                    const colors = ['cc-av-blue', 'cc-av-purple', 'cc-av-cyan', 'cc-av-indigo', 'cc-av-teal'];
                    const colorClass = colors[(acct.accountName || '').charCodeAt(0) % colors.length];
                    return (
                      <div key={acct.id} className="cc-cold-item"
                        onClick={() => acct.id && onNavigate && onNavigate('account-detail', acct.id)}
                        style={{ cursor: 'pointer' }}>
                        <div className={`cc-grid-person-avatar ${colorClass}`}>{initials}</div>
                        <div className="cc-cold-body">
                          <div className="cc-cold-name">{acct.accountName}</div>
                          <div className="cc-cold-meta">
                            {acct.hasActiveOpp && acct.oppName && (
                              <span className="cc-cold-opp">{acct.oppName}</span>
                            )}
                            {acct.signalCount > 0 && (
                              <span className="cc-cold-signals">{acct.signalCount} signal{acct.signalCount > 1 ? 's' : ''}</span>
                            )}
                          </div>
                        </div>
                        <div className="cc-cold-days">{acct.daysSince ? `${acct.daysSince}d` : 'Never'}</div>
                      </div>
                    );
                  })
                )}
              </div>
            </Card>

          </div>{/* /cc-bottom-right */}

        </div>{/* /cc-bottom-layout */}

      </div>{/* /cc-body */}
    </div>

      {/* People to Engage — slide-out panel */}
      {selectedPerson && (
        <div className="cc-modal-overlay" onClick={() => setSelectedPerson(null)}>
          <div className="cc-modal cc-modal--engage" onClick={e => e.stopPropagation()}>
            <div className="cc-modal-header">
              <div className="cc-engage-title">
                <span className="cc-engage-name">{selectedPerson.contactName}</span>
                {selectedPerson.title && <span className="cc-engage-role">{selectedPerson.title}</span>}
              </div>
              <button className="cc-modal-close" onClick={() => setSelectedPerson(null)}>✕</button>
            </div>

            <div className="cc-engage-body">
              {/* Account + Opportunity */}
              <div className="cc-engage-section">
                <div className="cc-engage-section-label">Account</div>
                <div className="cc-engage-section-value cc-engage-account-link"
                  onClick={() => { setSelectedPerson(null); onNavigate && onNavigate('account-detail', selectedPerson.accountId); }}>
                  {selectedPerson.accountName} →
                </div>
              </div>

              {selectedPerson.oppName && (
                <div className="cc-engage-section">
                  <div className="cc-engage-section-label">Related Opportunity</div>
                  <div className="cc-engage-section-value">{selectedPerson.oppName}</div>
                </div>
              )}

              {/* Why now — Victor's framing once the brief loads, else the signal headline */}
              {((parsedBrief && parsedBrief.whyNow) || selectedPerson.whyNow) && (
                <div className="cc-engage-section">
                  <div className="cc-engage-section-label">Why now</div>
                  <div className="cc-engage-section-value">{(parsedBrief && parsedBrief.whyNow) || selectedPerson.whyNow}</div>
                </div>
              )}

              {/* Victor's tailored brief — ANGLE + OPENER (Why now is shown above, not repeated here) */}
              <div className="cc-engage-section">
                <div className="cc-engage-section-label">Victor's brief</div>
                {briefLoading ? (
                  <div className="cc-engage-section-value" style={{ opacity: 0.7 }}>Victor is preparing a tailored brief…</div>
                ) : (parsedBrief && (parsedBrief.angle || parsedBrief.opener)) ? (
                  <div className="cc-engage-brief-window">
                    {parsedBrief.angle && (
                      <div>
                        <div className="cc-engage-brief-h">Angle</div>
                        <div>{parsedBrief.angle}</div>
                      </div>
                    )}
                    {parsedBrief.opener && (
                      <div style={{ marginTop: 10 }}>
                        <div className="cc-engage-brief-h">Opener</div>
                        <div>{parsedBrief.opener}</div>
                      </div>
                    )}
                  </div>
                ) : briefText ? (
                  <div className="cc-engage-brief-window">{briefText}</div>
                ) : (
                  <div className="cc-engage-brief-window">{selectedPerson.bestAngle || 'No brief available yet.'}</div>
                )}
              </div>

              {/* Primary action */}
              <div className="cc-engage-actions">
                <button className="cc-engage-btn cc-engage-btn--primary"
                  onClick={() => {
                    const p = selectedPerson;
                    setSelectedPerson(null);
                    const angle = briefText || p.bestAngle || '';
                    const msg = `Cyrano, draft a Signal-Led outreach email to ${p.contactName} at ${p.accountName}. ${p.oppName ? 'This relates to the ' + p.oppName + ' opportunity. ' : ''}Why now: ${p.whyNow || ''}.${angle ? ' Use this angle: ' + angle : ''} Make it specific, relevant, and compelling. Send it to staging when done.`;
                    onNavigate && onNavigate('floor', null, { type: 'nav', message: msg });
                  }}>
                  ✍️ Draft Outreach with Cyrano
                </button>
              </div>

              {/* Actions menu */}
              <div className="cc-engage-actions" style={{ flexDirection: 'column' }}>
                <button className="cc-engage-btn cc-engage-btn--secondary"
                  onClick={() => setOpenMenu(openMenu === 'actions' ? null : 'actions')}>
                  Actions ▾
                </button>
                {openMenu === 'actions' && (
                  <>
                    <button className="cc-engage-btn cc-engage-btn--secondary"
                      onClick={() => {
                        const p = selectedPerson;
                        setSelectedPerson(null);
                        const msg = `Victor, help me prep to engage ${p.contactName} at ${p.accountName}. ${p.oppName ? 'Opportunity: ' + p.oppName + '. ' : ''}Why now: ${p.whyNow || ''}.`;
                        onNavigate && onNavigate('floor', null, { type: 'nav', message: msg });
                      }}>
                      📞 Prep with Victor
                    </button>
                    <button className="cc-engage-btn cc-engage-btn--secondary"
                      onClick={() => handleCreateTask(selectedPerson)}>
                      ✅ Create Task
                    </button>
                    <button className="cc-engage-btn cc-engage-btn--secondary"
                      onClick={() => {
                        const aid = selectedPerson.accountId;
                        setSelectedPerson(null);
                        onNavigate && onNavigate('account-detail', aid);
                      }}>
                      🏢 Open Account
                    </button>
                  </>
                )}
              </div>

              {/* Manage menu */}
              <div className="cc-engage-actions" style={{ flexDirection: 'column' }}>
                <button className="cc-engage-btn cc-engage-btn--secondary"
                  onClick={() => setOpenMenu(openMenu === 'manage' ? null : 'manage')}>
                  Manage ▾
                </button>
                {openMenu === 'manage' && (
                  <>
                    <button className="cc-engage-btn cc-engage-btn--secondary"
                      onClick={() => handleEngageAction('done', selectedPerson)}>
                      ✓ Done
                    </button>
                    <button className="cc-engage-btn cc-engage-btn--secondary"
                      onClick={() => handleEngageAction('snooze', selectedPerson, { snoozeUntil: new Date(Date.now() + 7 * 86400000).toISOString() })}>
                      ⏰ Snooze 7d
                    </button>
                    <button className="cc-engage-btn cc-engage-btn--secondary"
                      onClick={() => handleEngageAction('dismiss', selectedPerson)}>
                      ✕ Dismiss
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedTask && (
        <TaskModal
          task={selectedTask}
          onClose={() => setSelectedTask(null)}
          onSave={() => { setSelectedTask(null); }}
          onDelete={() => { setSelectedTask(null); }}
          onNavigateAccount={(accountId) => {
            if (onNavigate) onNavigate('account-detail', accountId);
          }}
        />
      )}
      {newsAction && (
        <NewsActionPanel
          item={newsAction.item}
          onClose={() => setNewsAction(null)}
          onNavigate={onNavigate}
        />
      )}
      {storyModal && (
        <StoryModal
          item={storyModal}
          type={storyModal._type === 'intel' ? 'intel' : 'news'}
          onClose={() => setStoryModal(null)}
        />
      )}
      {csOpen && centerStage && (
        <CenterStageModal
          brief={centerStage}
          onClose={() => setCsOpen(false)}
          onNavigate={onNavigate}
        />
      )}
      {libraryOpen && (
        <CenterStageLibrary
          items={libraryItems}
          loading={libraryLoading}
          onClose={() => setLibraryOpen(false)}
          onPick={(brief) => { setLibraryBrief(brief); setLibraryOpen(false); }}
        />
      )}
      {libraryBrief && (
        <CenterStageModal
          brief={libraryBrief}
          onClose={() => setLibraryBrief(null)}
          onNavigate={onNavigate}
        />
      )}
    </>
  );
}
